import { Toggle } from 'base/ui/toggle';
import { observable } from 'mobx';
import React from 'react';
import { Badge } from './badge';
import {
  Button,
  ButtonPalette,
  RadioButtonGroup,
  VariantAndBackgroundProp,
} from './button';
import { Checkbox } from './checkbox';
import { ColoredIcon } from './colored_icon';
import { Dialog, DisableDialogLayer_PlaygroundOnly } from './dialog';
import { useDocumentTitle } from './document_title';
import {
  FreeLimitDialog,
  GenericErrorDialog,
  LoadingDialog,
} from './error_dialogs';
import { FavIcon } from './fav_icon';
import * as Icons from './icons';
import { FormInput } from './input';
import { Grow, Inline, Rows } from './layout';
import { LoadingDots } from './loading_dots';
import { MenuButton, MenuInternalForPlayground } from './menu';
import { menuDivider, menuItem, MenuModel, subMenu } from './menu_model';
import { NumberInput, NumberValue } from './number_input';
import styles from './playground.css';
import { ProgressBar } from './progress_bar';
import { ClickInTextarea, Textarea } from './textarea';
import { TickRow } from './tick_row';
import { ToastLayer, ToastNotification, ToastQueue } from './toast';
import { COLOR_TOKENS } from './tokens';
import * as Typography from './typography';
import {
  TextSmall,
  TextTiny,
  TextTinyLight,
  Title5,
  Title6,
} from './typography';

import { delay } from 'base/helpers';
import featureGatePagesImage from '../extensions/feature-gate-pages.webp';
import featureGateProjectsImage from '../extensions/feature-gate-projects.webp';
import featureGateTeamImage from '../extensions/feature-gate-team.webp';
import { FeedbackForm } from './feedback_form';
import { SelectButton } from './select';
import { TabList } from './tabs';

// This needs to be here to bundle the images used in the extension
function ExtensionFeatureGateImagesPlayground() {
  return (
    <Inline spacing={8}>
      <img src={featureGateTeamImage} />
      <img src={featureGatePagesImage} />
      <img src={featureGateProjectsImage} />
    </Inline>
  );
}

export function PlaygroundSectionTitle({ label }: { label: string }) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div id={slug}>
      <Inline spacing={8} yalign="center">
        <Title6>{label}</Title6>
        <Button
          icon={Icons.IconLink}
          variant="subtle"
          href={`#${slug}`}
          tooltip="Link to this section"
        />
      </Inline>
    </div>
  );
}

function BadgesPlayground() {
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Badges" />
      <Inline spacing={16}>
        <Badge label="Gradient" variant="gradient" />
        <Badge label="Something grey" variant="grey2" />
        <Badge label="Something orange" variant="orange" />
        <Badge
          label="Click me"
          variant="grey2"
          onRemove={() => alert('clicked')}
        />
      </Inline>
    </Rows>
  );
}

function LoadingDotsPlayground() {
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Loading Dots" />
      <LoadingDots />
    </Rows>
  );
}

function ButtonsPlayground() {
  const variants: VariantAndBackgroundProp[] = [
    { variant: 'primary', gradient: true },
    { variant: 'primary' },
    { variant: 'primary', background: 'ai' },
    { variant: 'primary', background: 'global' },
    { variant: 'primary', background: 'negative' },
    { variant: 'secondary' },
    { variant: 'secondary', background: 'transparent' },
    { variant: 'secondary', background: 'grey3' },
    { variant: 'tertiary' },
    { variant: 'tertiary', background: 'white' },
    { variant: 'tertiary', background: 'grey2' },
    { variant: 'subtle' },
    { variant: 'select' },
  ];

  const mapVariants = (
    props: Pick<
      React.ComponentProps<typeof Button>,
      'icon' | 'checked' | 'size' | 'tooltip'
    >,
    variantLabel = false,
  ) => (
    <Inline spacing={8} wrap>
      {variants.map((vp, i) => (
        <Button
          key={i}
          {...vp}
          {...props}
          label={
            variantLabel
              ? `${vp.variant} ${vp.background || ''} ${
                  vp.gradient ? 'gradient' : ''
                }`.trim()
              : (undefined as any as string)
          }
        />
      ))}
    </Inline>
  );
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <Rows spacing={16}>
      <PlaygroundSectionTitle label="Buttons" />
      <Rows spacing={8}>
        <TextSmall>Large Text Buttons</TextSmall>
        {mapVariants({ size: 'large', tooltip: 'tooltip content' }, true)}
      </Rows>
      <Rows spacing={8} xalign="start">
        <TextSmall>Loading Buttons</TextSmall>
        <Button label="Big" size="large" variant="primary" loading={false} />
        <Button label="Big" size="large" variant="primary" loading={true} />
        <Button
          label="Medium"
          variant="primary"
          background="ai"
          loading={false}
        />
        <Button
          label="Medium"
          variant="primary"
          background="ai"
          loading={true}
        />
        <Button
          label="Click to load"
          loading={isLoading}
          onClick={() => {
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 2000);
          }}
        />
      </Rows>
      <Rows spacing={8}>
        <TextSmall>Medium Text & Icon Buttons</TextSmall>
        {mapVariants({ icon: Icons.IconStars }, true)}
      </Rows>
      <Rows spacing={8}>
        <TextSmall>Small Text & Icon Buttons</TextSmall>
        {mapVariants({ icon: Icons.IconEnter, size: 'small' }, true)}
      </Rows>
      <Rows spacing={8}>
        <TextSmall>Medium Icon Buttons</TextSmall>
        {mapVariants({ icon: Icons.IconAdd })}
      </Rows>
      <Rows spacing={8}>
        <TextSmall>
          Small Checked Icon Buttons (not all variants supported)
        </TextSmall>
        {mapVariants({ icon: Icons.IconClear, size: 'small', checked: true })}
      </Rows>
      <Rows spacing={8}>
        <TextSmall>Medium Text Buttons with shortcuts</TextSmall>
        <Inline spacing={8} wrap>
          <Button label="Re-run" shortcut="R" />
          <Button label="Stop" shortcut="Esc" />
        </Inline>
      </Rows>
    </Rows>
  );
}

export function ButtonPalettePlayground() {
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Button Palette" />
      <Inline spacing={0}>
        <ButtonPalette>
          <Button label="Secondary Button" variant="secondary" />
          <RadioButtonGroup
            options={[
              { label: 'Option 1', value: '1' },
              { label: 'Option 2', value: '2' },
              { label: 'Option 3', value: '3' },
            ]}
            value="2"
          />
        </ButtonPalette>
      </Inline>
    </Rows>
  );
}

function TickRowsPlayground() {
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Tick Rows" />
      <TickRow size="large">A large tick row</TickRow>
      <TickRow size="medium">A medium tick row</TickRow>
      <TickRow size="small">A small tick row</TickRow>
    </Rows>
  );
}

function CheckboxesPlayground() {
  const [checked, setChecked] = React.useState(false);
  const setOpposite = (v: boolean) => setChecked(!v);

  const id1 = React.useId();
  const id2 = React.useId();

  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Checkboxes" />
      <Checkbox
        id={id1}
        value={checked}
        onChange={setChecked}
        label="Normal checkbox"
      />
      <Checkbox
        id={id2}
        value={!checked}
        onChange={setOpposite}
        label="Opposite of the above"
      />
      <Checkbox
        value={checked}
        onChange={setChecked}
        label="Radio button A"
        type="radio"
      />
      <Checkbox
        value={!checked}
        onChange={setOpposite}
        label="Radio button B"
        type="radio"
      />
    </Rows>
  );
}

function ProgressBarsPlayground() {
  const TOTAL_STEPS = 3;
  const [currentStep, setCurrentStep] = React.useState(0);
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Progress bars" />
      <ProgressBar
        ariaLabel="A progress bar"
        currentStep={0}
        totalSteps={TOTAL_STEPS}
      />
      <ProgressBar
        ariaLabel="A progress bar"
        currentStep={1}
        totalSteps={TOTAL_STEPS}
      />
      <ProgressBar
        ariaLabel="A progress bar"
        currentStep={2}
        totalSteps={TOTAL_STEPS}
      />
      <ProgressBar
        ariaLabel="A progress bar"
        currentStep={3}
        totalSteps={TOTAL_STEPS}
      />
      <Inline spacing={8} yalign="center">
        <Button
          size="small"
          variant="subtle"
          label="Previous"
          onClick={() =>
            setCurrentStep((currentStep) => Math.max(0, currentStep - 1))
          }
        />
        <Button
          size="small"
          variant="subtle"
          label="Next"
          onClick={() =>
            setCurrentStep((currentStep) =>
              Math.min(TOTAL_STEPS, currentStep + 1),
            )
          }
        />
        <Grow>
          <ProgressBar
            ariaLabel="A progress bar"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            currentStepDurationMs={10000}
          />
        </Grow>
      </Inline>
    </Rows>
  );
}

function MenusPlayground() {
  const menuModel: MenuModel = [
    menuItem({
      label: 'Copy',
      shortcut: '⌘C',
    }),
    menuDivider(),
    { type: 'text', label: 'Some very long but very static text yay' },
    menuItem({ label: 'Option 2' }),
    subMenu({
      label: 'Choose a Color',
      menu: [
        menuItem({ label: 'Red' }),
        menuItem({ label: 'Green' }),
        menuItem({ label: 'Blue' }),
      ],
    }),
    subMenu({
      label: 'Choose a Font',
      menu: [
        menuItem({ label: 'Arial' }),
        menuItem({ label: 'Helvetica' }),
        menuItem({ label: 'Times New Roman' }),
      ],
    }),
    menuItem({
      label: 'Option 3',
      shortcut: 'X',
    }),
  ];

  const menuInternalProps = {
    isRoot: true,
    closeRootMenu: () => alert('close root menu'),
    closeThisMenu: () => alert('close this menu'),
    className: styles.menuInternal,
  };

  return (
    <Rows spacing={8} xalign="start">
      <PlaygroundSectionTitle label="Menus" />
      <MenuButton icon={Icons.IconOption} menu={menuModel} />
      <MenuInternalForPlayground model={menuModel} {...menuInternalProps} />
      <MenuInternalForPlayground
        model={menuModel}
        {...menuInternalProps}
        search={{
          placeholder: 'Something ai blah blah blah',
          iconStart: <ColoredIcon icon={Icons.IconStars} color="colorRed" />,
          button: {
            tooltip: 'Use custom prompt',
            onSearch: (value: string) => alert(`custom: ${value}`),
          },
        }}
        menuVariant="disjointed"
      />
    </Rows>
  );
}

function NumberInputPlayground() {
  const [value, setValue] = React.useState<NumberValue>(5);
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Number Inputs" />
      <TextSmall>
        <NumberInput value={value} onChange={setValue} min={0} max={10} />
      </TextSmall>
    </Rows>
  );
}

function FormInputPlayground() {
  const [value, setValue] = React.useState('Some text yay');
  const [selectedValue, setSelectedValue] = React.useState('Option 1');
  const isSelected = (label: string) => label === selectedValue;
  const makeOption = (index: number) => {
    const value = `Option ${index}`;
    return menuItem({
      label: value,
      onClick: () => setSelectedValue(value),
      checked: isSelected(value),
    });
  };
  const menu = Array.from({ length: 3 }, (_, index) => makeOption(index + 1));
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Form Inputs" />
      <Inline spacing={8} wrap>
        <FormInput
          value={value}
          onChange={setValue}
          placeholder="Placeholder"
          type="text"
        />
        <FormInput
          value={value}
          onChange={setValue}
          placeholder="Placeholder"
          type="text"
          variant="darker"
        />
        <FormInput
          iconStart={Icons.IconSearch}
          buttonEnd={
            <Button
              size="small"
              variant="subtle"
              icon={Icons.IconClear}
              onClick={() => setValue('')}
            />
          }
          value={value}
          onChange={setValue}
          placeholder="Search"
          type="search"
        />
        <FormInput
          iconStart={Icons.IconSearch}
          buttonEnd={
            <Button
              size="small"
              variant="subtle"
              icon={Icons.IconClear}
              onClick={() => setValue('')}
            />
          }
          value={value}
          onChange={setValue}
          placeholder="Search (wrapped)"
          type="textarea"
        />
        <FormInput
          value={value}
          onChange={setValue}
          placeholder="Email"
          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
          type="email"
        />
        <FormInput
          value={value}
          onChange={setValue}
          placeholder="Password"
          type="password"
        />
        <FormInput value="" placeholder="Empty & Readonly" type="text" />
        <FormInput
          iconStart={Icons.IconMap}
          buttonEnd={
            <Button size="small" variant="primary" icon={Icons.IconAdd} />
          }
          value={value}
          onChange={setValue}
          placeholder="Borderless input"
          type="text"
          variant="borderless"
        />
        <FormInput
          iconStart={Icons.IconMap}
          buttonEnd={
            <Button size="small" variant="primary" icon={Icons.IconAdd} />
          }
          value={value}
          onChange={setValue}
          placeholder="Borderless textarea input"
          type="textarea"
          variant="borderless"
        />
        <SelectButton menu={menu} defaultLabel="Select" />
        <SelectButton
          menu={menu}
          defaultLabel="Search"
          searchPlaceholder="Select..."
        />
      </Inline>
    </Rows>
  );
}

function TextareaPlayground() {
  const [longValue, setLongValue] = React.useState(
    'A text area\n\nWith the lot',
  );
  const [emptyValue, setEmptyValue] = React.useState('');
  const [invisibleValue, setInvisibleValue] = React.useState(
    'A popout dark themed text area (click-in-to-edit)',
  );
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Textareas" />
      <TextSmall tag="div">
        <Inline spacing={8}>
          <Textarea value={longValue} onChange={setLongValue} />
          <Textarea
            placeholder="Placeholder"
            value={emptyValue}
            onChange={setEmptyValue}
          />
        </Inline>
      </TextSmall>
      <div className={styles.dark}>
        <TextSmall color="white" tag="div">
          <Inline spacing={8}>
            <ClickInTextarea
              variant="popoutDark"
              enableClickIn={true}
              value={invisibleValue}
              onChange={setInvisibleValue}
            />
            <ClickInTextarea
              variant="popoutDark"
              enableClickIn={true}
              placeholder="Placeholder"
              value={emptyValue}
              onChange={setEmptyValue}
            />
          </Inline>
        </TextSmall>
      </div>
    </Rows>
  );
}

function TabListPlayground() {
  const [selectedValue, setSelectedValue] = React.useState('tab-one');
  return (
    <Rows spacing={8}>
      <TabList
        options={[
          { value: 'tab-one', label: 'Tab one' },
          { value: 'tab-two', label: 'Tab two' },
        ]}
        selectedValue={selectedValue}
        onChange={(value) => setSelectedValue(value)}
      />
      <TextSmall>Selected tab: {selectedValue}</TextSmall>
    </Rows>
  );
}

function DialogPlayground() {
  return (
    <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
      <Rows spacing={8}>
        <PlaygroundSectionTitle label="Dialogs" />
        <FreeLimitDialog />
        <LoadingDialog title="Loading something..." />
        <GenericErrorDialog
          title="Error doing something"
          error={new Error('foo bar')}
          dismiss={() => alert('dismissed error dialog')}
        />
      </Rows>
    </DisableDialogLayer_PlaygroundOnly.Provider>
  );
}

function IconPlayground() {
  const entries = Object.entries(Icons).sort(([a], [b]) => a.localeCompare(b));
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Icons" />
      <Inline spacing={8} wrap>
        {entries.map(([name, Icon]) => (
          <div key={name} className={styles.iconHolder}>
            <div className={styles.icon}>
              <Icon />
            </div>
            <TextTiny>{name}</TextTiny>
          </div>
        ))}
      </Inline>
    </Rows>
  );
}

function TypographyPlayground() {
  const entries = Object.entries(Typography).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Typography" />
      {entries.map(([name, Typography]) => (
        <Typography key={name}>Typography: {name}</Typography>
      ))}
    </Rows>
  );
}

function TokensPlayground() {
  const entries = Object.entries(COLOR_TOKENS);
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Color Tokens" />
      <Inline spacing={8} wrap>
        {entries.map(([name, hex]) => (
          <div key={name} className={styles.iconHolder}>
            <div
              className={styles.colorTester}
              style={{ backgroundColor: hex }}
            />
            <Rows spacing={0} xalign="center">
              <TextTiny>{name}</TextTiny>
              <TextTinyLight>{hex}</TextTinyLight>
            </Rows>
          </div>
        ))}
      </Inline>
    </Rows>
  );
}

function FeedbackFormPlayground() {
  const onSubmit = async () => {
    await delay(2_000);
  };
  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Feedback form" />
      <FeedbackForm onSubmit={onSubmit} parentVariant="panel" />
      <DisableDialogLayer_PlaygroundOnly.Provider value={true}>
        <Dialog title="Feedback" onClose={() => void 0}>
          <FeedbackForm onSubmit={onSubmit} parentVariant="dialog" />
        </Dialog>
      </DisableDialogLayer_PlaygroundOnly.Provider>
    </Rows>
  );
}

const RANDOM_TOAST_GROUP = Symbol('random toast group');

function ToastPlayground() {
  const [queue] = React.useState(() => new ToastQueue());
  const [showPersistent] = React.useState(() => observable.box(false));
  React.useEffect(
    () =>
      queue.addPersistent(() =>
        showPersistent.get()
          ? {
              label: 'Oh no there is an unsaved change',
              action: {
                label: 'Save',
                onClick: () => queue.push({ label: 'Clicked Save' }),
              },
            }
          : undefined,
      ),
    [queue, showPersistent],
  );

  return (
    <Rows spacing={8}>
      <PlaygroundSectionTitle label="Toasts" />
      <ToastNotification label="Your clipboard is empty" />
      <ToastNotification
        label="Heads up! You are editing an unsaved project."
        action={{
          label: 'Save',
          onClick: () => queue.push({ label: 'Clicked' }),
        }}
      />
      <ToastNotification
        label="Sitemap imported from guzmanygomez.com.au"
        customRight={<FavIcon domain="guzmanygomez.com.au" size="small" />}
      />
      <Button
        onClick={() =>
          queue.push({
            label: 'A toast at the bottom ' + Math.round(Math.random() * 100),
            group: RANDOM_TOAST_GROUP,
          })
        }
        label="Show floating toast (group)"
      />
      <Button
        onClick={() => showPersistent.set(!showPersistent.get())}
        label="Toggle persistent toast"
      />
      <ToastLayer queue={queue} />
    </Rows>
  );
}

function TogglePlayground() {
  const [value, setValue] = React.useState(false);

  return (
    <Rows xalign="start">
      <PlaygroundSectionTitle label="Toggles" />
      <Toggle value={value} onChange={setValue} />
      <div>
        <Rows>
          <Toggle label="Default" value={value} onChange={setValue} />
          <Toggle
            label="Green"
            value={value}
            onChange={setValue}
            color="green"
          />
          <Toggle
            label="Disabled"
            value={value}
            onChange={setValue}
            disabledReason="You must construct additional pylons!"
          />
        </Rows>
      </div>
    </Rows>
  );
}

export function UiPlayround() {
  useDocumentTitle('UI Playground');
  return (
    <div className={styles.playground}>
      <Rows spacing={32}>
        <Title5>UI Element Playground</Title5>
        <BadgesPlayground />
        <LoadingDotsPlayground />
        <ButtonsPlayground />
        <ButtonPalettePlayground />
        <TickRowsPlayground />
        <CheckboxesPlayground />
        <ProgressBarsPlayground />
        <TogglePlayground />
        <NumberInputPlayground />
        <FormInputPlayground />
        <TextareaPlayground />
        <TabListPlayground />
        <MenusPlayground />
        <DialogPlayground />
        <IconPlayground />
        <TypographyPlayground />
        <TokensPlayground />
        <ExtensionFeatureGateImagesPlayground />
        <FeedbackFormPlayground />
        <ToastPlayground />
      </Rows>
    </div>
  );
}
